// Shared donut for Meta Ads Demographics (Gender / Age) — thin wrapper
// around the shared DonutWithLegend scaffold (extracted 2026-08-10,
// PHASE-C3, from the center-total+legend shape this file and
// CallOutcomesDonut.tsx had independently built byte-for-byte identically;
// see that file's own module docstring for the extraction). This file now
// owns only the DEMOGRAPHICS-specific parts: the color STRATEGY per caller
// (gender is categorical identity — Female/Male/Uncategorized carry no
// order; age is ordinal — 13-17 < 18-24 < ... < 65+ IS the information —
// so they need different palette shapes) and the tooltip/legend text.
//
// Palette validated against the dataviz skill's validate_palette.js
// (categorical: all-pairs mode, since a donut+legend lets a viewer compare
// any two slices, not just physically adjacent wedges; ordinal: the
// skill's single-hue sequential ramp, clamped to the documented 2:1-safe
// band [step 250, step 600] so it clears contrast on both light and dark
// surfaces). Fixed hex, not CSS vars — same reasoning as
// CallOutcomesDonut's OUTCOME_COLORS: identity must read the same in both
// themes, unlike --color-coral/--color-teal which intentionally swap value
// across them.

import type { LabeledCount } from "@/types/clientAnalytics";
import { DonutWithLegend, type DonutLegendSlice } from "@/components/client-analytics/DonutWithLegend";

export interface DemographicSlice extends LabeledCount {
  color: string;
}

export function DemographicDonut({
  title,
  slices,
  centerLabel = "Clicks",
  emptyMessage,
}: {
  title: string;
  slices: DemographicSlice[];
  centerLabel?: string;
  emptyMessage?: string;
}) {
  const legendSlices: DonutLegendSlice[] = slices.map((s) => ({
    key: s.label,
    label: s.label,
    value: s.value,
    color: s.color,
  }));
  return (
    <DonutWithLegend
      slices={legendSlices}
      centerLabel={centerLabel}
      emptyMessage={emptyMessage ?? `No ${title.toLowerCase()} data yet`}
      tooltipFormatter={(value, name, total) => [
        `${value.toLocaleString()} (${Math.round((value / total) * 100)}%)`,
        name,
      ]}
      legendValueFormatter={(s) => s.value.toLocaleString()}
    />
  );
}

// ---------------------------------------------------------------------------
// Gender — categorical identity, 3 known buckets. Fixed by LABEL, not by
// value-rank ("color follows the entity, never its rank" — a client where
// Male > Female must not swap which color means which gender). Slots 1-3 of
// the dataviz skill's validated 8-hue categorical order (blue/orange/aqua),
// the only 3 that clear the ALL-PAIRS floor in both light and dark modes —
// exactly the donut+legend viewing pattern here, not just adjacent wedges.
const GENDER_COLOR: Record<string, string> = {
  female: "#2a78d6", // blue — slot 1
  male: "#eb6834", // orange — slot 2
};
const GENDER_FALLBACK_COLOR = "#1baf7a"; // aqua — slot 3, "Uncategorized" and anything unrecognized

// AA sends raw, lowercase, un-relabeled provider values ("female"/"male"/
// "unknown" — confirmed live by demo-backend, who deliberately does NOT
// relabel provider values in the backend). Display formatting is this
// component's job: title-case the two known genders, and "unknown" reads
// as "Uncategorized" (matching AA's own report page wording) rather than
// the raw lowercase provider string.
const GENDER_DISPLAY: Record<string, string> = {
  female: "Female",
  male: "Male",
};

function displayGenderLabel(rawLabel: string): string {
  return GENDER_DISPLAY[rawLabel.trim().toLowerCase()] ?? "Uncategorized";
}

export function buildGenderSlices(byGender: LabeledCount[] | null | undefined): DemographicSlice[] {
  if (!byGender) return [];
  return byGender
    .map((row) => ({
      label: displayGenderLabel(row.label),
      value: row.value,
      color: GENDER_COLOR[row.label.trim().toLowerCase()] ?? GENDER_FALLBACK_COLOR,
    }))
    .sort((a, b) => b.value - a.value);
}

// ---------------------------------------------------------------------------
// Age — ordinal, not categorical: the bands have an inherent order
// (youngest to oldest) and THAT order is the information, so this is a
// single-hue sequential ramp (dataviz skill's default blue ramp), not a
// rainbow of unrelated hues. Steps are clamped to [250, 600] — the skill's
// documented ordinal-safe band that clears 2:1 contrast on BOTH a light and
// a dark chart surface with the same hex (no per-theme swap needed).
// "Unknown"/unrecognized labels aren't a point on the age axis at all (an
// unknown age isn't "older than 65+"), so they get their own flat neutral
// grey — matching CallOutcomesDonut's "unclassified" treatment — rather
// than occupying a ramp step.
const AGE_ORDER = ["13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const AGE_RAMP = ["#86b6ef", "#6da7ec", "#5598e7", "#3987e5", "#2a78d6", "#1c5cab", "#184f95"];
const AGE_UNKNOWN_COLOR = "#9CA3AF"; // light grey — Core-derived neutral, matches CallOutcomesDonut's "unclassified"

function ageBandIndex(label: string): number {
  // Normalize en/em/figure dash variants (U+2010-U+2015) to a plain hyphen
  // so "55–64" (AA sometimes uses an en dash) still matches "55-64".
  const normalized = label.trim().toLowerCase().replace(/[‐-―]/g, "-");
  return AGE_ORDER.indexOf(normalized);
}

export function buildAgeSlices(byAge: LabeledCount[] | null | undefined): DemographicSlice[] {
  if (!byAge) return [];
  // Unrecognized labels (idx -1, e.g. "Unknown") sort last — they're not a
  // point on the age axis, so they belong after the oldest real band, never
  // before the youngest one.
  const sortKey = (label: string) => {
    const idx = ageBandIndex(label);
    return idx >= 0 ? idx : AGE_ORDER.length;
  };
  return [...byAge]
    .sort((a, b) => sortKey(a.label) - sortKey(b.label))
    .map((row) => {
      const idx = ageBandIndex(row.label);
      return { ...row, color: idx >= 0 ? AGE_RAMP[idx] : AGE_UNKNOWN_COLOR };
    });
}
